import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { IonAlert, IonButton, IonIcon } from '@ionic/react';
import { bagHandleOutline, calendarOutline, closeOutline, locationOutline } from 'ionicons/icons';
import moment from 'moment';
import Loading from '../../../components/spinner';
import { withTranslation } from '../../../lib/translate';
import Layout from '../../../components/layout';
import { NormalText, SmallText, Spacer, StrongText, Subtitle, Title } from '../../../components/common';
import NoData from '../../../components/noData';
import Incrementer from '../../../components/incrementer';
import Basket from '../../../lib/basket';
import { getEvent } from '../../../store/actions';
import { CREATE_ORDER } from '../../../store/constants';
import { forwardTo } from '../../../lib/utils';
import { getConfig } from '../../../appConfig';
import { Capacitor } from '@capacitor/core';
import './index.css';

const isWeb = () => Capacitor.getPlatform() === 'web';

class EventCheckout extends React.Component {
  state = { ticketToRemove: null };

  componentDidMount() {
    const { restaurants } = this.props;
    Basket.setRestaurant(restaurants.find((restaurant) => restaurant.is_event_venue));
    // refresh availability so the per-order limits are up to date, one event at a time
    this.pendingEventIds = this.getEventIds();
    this.fetchNextEvent();
  }

  componentDidUpdate(prevProps) {
    if (this.props.event && prevProps.event !== this.props.event) {
      this.fetchNextEvent();
    }
  }

  fetchNextEvent = () => {
    const eventId = (this.pendingEventIds || []).shift();
    if (eventId != null) {
      this.props.dispatch(getEvent(eventId));
    }
  };

  getEventIds = () =>
    this.getTickets().reduce((ids, ticket) => {
      const eventId = ticket.basketItem.item.event_id;
      if (eventId != null && !ids.some((id) => String(id) === String(eventId))) {
        ids.push(eventId);
      }
      return ids;
    }, []);

  getTickets = () =>
    Basket.getItems()
      .map((basketItem, index) => ({ basketItem, index }))
      .filter(({ basketItem }) => basketItem.item?.is_ticket && basketItem.quantity > 0);

  getGroups = () => {
    const groups = [];
    this.getTickets().forEach((ticket) => {
      const eventId = ticket.basketItem.item.event_id;
      let group = groups.find((entry) => String(entry.eventId) === String(eventId));
      if (!group) {
        group = { eventId, item: ticket.basketItem.item, tickets: [] };
        groups.push(group);
      }
      group.tickets.push(ticket);
    });
    return groups;
  };

  // until the event has been refreshed the limit is unknown - leave it uncapped rather
  // than locking the quantity to whatever is already in the basket
  getMaxQuantity = (ticket) => {
    const max = ticket.basketItem.item.max_quantity;
    return typeof max === 'number' ? max : null;
  };

  getEventMaxQuantity = (group) => group.item.event_max_quantity || 0;

  // the event caps the whole order, so a ticket can be limited before its own maximum is reached
  getQuantityLimit = (ticket, group) => {
    const ticketMax = this.getMaxQuantity(ticket);
    const eventMax = this.getEventMaxQuantity(group);
    if (!eventMax) {
      return { max: ticketMax, limitedByEvent: false };
    }
    const selected = group.tickets.reduce((sum, entry) => sum + entry.basketItem.quantity, 0);
    const remaining = Math.max(eventMax - (selected - ticket.basketItem.quantity), 0);
    return {
      max: ticketMax === null ? remaining : Math.min(ticketMax, remaining),
      limitedByEvent: selected >= eventMax,
    };
  };

  getTotalCount = () =>
    this.getTickets().reduce((sum, ticket) => sum + ticket.basketItem.quantity, 0);

  getTotalPrice = () =>
    this.getTickets().reduce(
      (sum, ticket) =>
        sum + ticket.basketItem.quantity * (parseFloat(ticket.basketItem.item.productPrice) || 0),
      0,
    );

  setQuantity = (ticket, value) => Basket.changeItemQuantity(ticket.index, value);

  removeTicket = () => {
    const { ticketToRemove } = this.state;
    this.setState({ ticketToRemove: null }, () => {
      if (ticketToRemove) {
        Basket.changeItemQuantity(ticketToRemove.index, 0);
      }
    });
  };

  addMoreTickets = (group) => forwardTo('/event-tickets', { eventId: group.eventId });

  backHandler = () => {
    const groups = this.getGroups();
    if (groups.length === 1) {
      forwardTo('/event-tickets', { eventId: groups[0].eventId });
      return;
    }
    forwardTo('/events');
  };

  goToCheckout = () => {
    if (this.getTotalCount() === 0) {
      return;
    }
    Basket.setUUID(`${this.props.profile.id}_${Date.now()}`);
    if (!isWeb() && getConfig()?.nativePayment?.isVisible) {
      this.props.dispatch({ type: CREATE_ORDER, paymentType: 'NativePayment' });
    } else {
      forwardTo('/checkout');
    }
  };

  renderGroup = (group) => {
    const { __ } = this.props;
    const { item } = group;
    const startTime = item.event_start_time ? moment(item.event_start_time) : null;

    return (
      <div key={`event-${group.eventId}`} className="event-checkout-group">
        <div className="event-checkout-event box-wrapper">
          {item.image && (
            <div
              className="event-checkout-event-image"
              style={{ backgroundImage: `url(${item.image})` }}
            />
          )}
          <div className="event-checkout-event-info">
            <StrongText className="block">{item.event_name}</StrongText>
            {startTime && (
              <div className="event-checkout-event-meta">
                <IonIcon icon={calendarOutline} />
                <SmallText>{startTime.format('ddd MMMM D, YYYY')}</SmallText>
              </div>
            )}
            {item.venue_name && (
              <div className="event-checkout-event-meta">
                <IonIcon icon={locationOutline} />
                <SmallText>{item.venue_name}</SmallText>
              </div>
            )}
          </div>
        </div>
        <Spacer size={1} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Subtitle>{__('Your tickets')}</Subtitle>
          <div className="pointer" onClick={() => this.addMoreTickets(group)}>
            <StrongText >{__('Add more tickets')}</StrongText>
          </div>
        </div>
        <Spacer size={0} />
        {group.tickets.map((ticket) => {
          const { basketItem } = ticket;
          const price = parseFloat(basketItem.item.productPrice) || 0;
          const { max, limitedByEvent } = this.getQuantityLimit(ticket, group);

          return (
            <div key={basketItem.item.sku} className="event-checkout-ticket box-wrapper">
              <div className="event-checkout-ticket-header">
                <div>
                  <StrongText className="block">{basketItem.item.ticket_tier_name}</StrongText>
                  <NormalText className="block">
                    {__('{price} each', { price: Basket.formatPrice(price, true) })}
                  </NormalText>
                </div>
                <IonIcon
                  className="pointer event-checkout-remove"
                  icon={closeOutline}
                  onClick={() => this.setState({ ticketToRemove: ticket })}
                />
              </div>
              <div className="event-checkout-ticket-footer">
                <div className='event-checkout-ticket-actions'>

                  <div className="event-checkout-qty">
                    <Incrementer
                      quantity={basketItem.quantity}
                      step={1}
                      allowNegative={false}
                      minLimit={1}
                      maxLimit={max}
                      onUpdate={(val) => this.setQuantity(ticket, val)}
                    />
                  </div>
                  {limitedByEvent ? (
                    <SmallText className="block event-checkout-max">
                      {__('Max {count} per event', { count: this.getEventMaxQuantity(group) })}
                    </SmallText>
                  ) : (
                    this.getMaxQuantity(ticket) > 0 && (
                      <SmallText className="block event-checkout-max">
                        {__('Max {count} per person', { count: this.getMaxQuantity(ticket) })}
                      </SmallText>
                    )
                  )}
                </div>
                <div>
                  <StrongText>{Basket.formatPrice(price * basketItem.quantity, true)}</StrongText>

                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const { __ } = this.props;
    const groups = this.getGroups();
    const totalCount = this.getTotalCount();
    const formattedTotal = Basket.formatPrice(this.getTotalPrice(), true);

    return (
      <Loading>
        <Layout
          title={__('Your tickets')}
          headerWithTitle
          color="transparent"
          backHandler={this.backHandler}
        >
          <div
            className={`absolute-content event-checkout scrollable-y${totalCount > 0 ? ' has-footer' : ''
              }`}
          >
            <Title className={'web-only'}>{__('Your tickets')}</Title>
            {totalCount === 0 ? (
              <div className="event-checkout-empty">
                <NoData label="You have no tickets in your basket" />
                <Spacer size={2} />
                <IonButton expand="block" onClick={() => forwardTo('/events')}>
                  {__('Browse events')}
                </IonButton>
              </div>
            ) : (
              groups.map((group) => this.renderGroup(group))
            )}
          </div>
          {totalCount > 0 && (
            <div className="event-checkout-footer">
              <div className="event-checkout-total">
                <IonIcon color="primary" icon={bagHandleOutline} />
                {totalCount == 1 ? (
                  <SmallText>
                    {__('{count} ticket · {price}', { count: totalCount, price: formattedTotal })}
                  </SmallText>
                ) : (
                  <SmallText>
                    {__('{count} tickets · {price}', { count: totalCount, price: formattedTotal })}
                  </SmallText>
                )}
              </div>
              <IonButton
                expand="block"
                className="event-checkout-button"
                onClick={this.goToCheckout}
              >
                {__('Continue to Checkout · {price}', { price: formattedTotal })}
              </IonButton>
            </div>
          )}
          <IonAlert
            isOpen={!!this.state.ticketToRemove}
            onDidDismiss={() => this.setState({ ticketToRemove: null })}
            className="remove-title-update"
            header={__('Are you sure you want to remove this ticket?')}
            buttons={[
              {
                text: __('Yes'),
                cssClass: 'primary',
                handler: this.removeTicket,
              },
              {
                text: __('No'),
                role: 'cancel',
                cssClass: 'secondary link',
                handler: () => this.setState({ ticketToRemove: null }),
              },
            ]}
          />
        </Layout>
      </Loading>
    );
  }
}

const stateToProps = (store) => {
  return {
    basketUpdated: store.orders.basketUpdated,
    event: store.orders.event,
    profile: store.profile.profile,
    restaurants: store.restaurants.restaurants,
  };
};

export default connect(stateToProps)(withRouter(withTranslation(EventCheckout)));
